import { Link } from "react-router";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { isOn } from "@/modules/apps/moduleDependencies";
import type { ModuleChange, ModuleRow } from "@/modules/apps/ModulesManager";
import { MODULES, type ModuleId, type NomiModule } from "@/modules/registry";

const joinNames = (ids: ModuleId[]) =>
  ids.map((id) => MODULES[id].name).join(", ");

/**
 * Switches for a module's features (Apps landing page and the platform
 * console). A feature that needs another app can only be switched on once
 * the company has that app; nothing is turned on behind the admin's back.
 */
export const ModuleFeatureSwitches = ({
  module,
  rows,
  busy,
  onChange,
  variant = "card",
}: {
  module: NomiModule;
  rows: ModuleRow[];
  busy?: boolean;
  onChange: (changes: ModuleChange[]) => void;
  /** card: Apps page (links to the missing app); compact: console. */
  variant?: "card" | "compact";
}) => {
  const features = module.features ?? [];
  if (!features.length) return null;
  const row = rows.find((r) => r.module_id === module.id);

  return (
    <div className="flex flex-col gap-2">
      {features.map((feature) => {
        const missing = (feature.requires ?? []).filter(
          (id) => !isOn(rows, id),
        );
        const saved = row?.options?.[feature.key] === true;
        const blocked = missing.length > 0;
        return (
          <div
            key={feature.key}
            className={cn(
              "flex items-center justify-between gap-3",
              variant === "card"
                ? "rounded-xl border bg-card px-4 py-3"
                : "rounded-md bg-muted/50 px-3 py-2",
            )}
          >
            <div className="min-w-0">
              <p
                className={cn(
                  "text-sm font-medium",
                  blocked && "text-muted-foreground",
                )}
              >
                {feature.label}
              </p>
              <p className="text-xs text-muted-foreground">
                {feature.description}
              </p>
              {blocked ? (
                <p className="mt-1 text-xs text-warning-text">
                  {saved ? "Paused — needs " : "Needs "}
                  {variant === "card" ? (
                    missing.map((id, index) => (
                      <span key={id}>
                        {index > 0 ? ", " : null}
                        <Link
                          to={`/apps/${id}`}
                          className="font-medium underline underline-offset-2"
                        >
                          {MODULES[id].name}
                        </Link>
                      </span>
                    ))
                  ) : (
                    <span className="font-medium">{joinNames(missing)}</span>
                  )}
                  . Turn it on first.
                </p>
              ) : null}
            </div>
            <Switch
              checked={saved && !blocked}
              disabled={busy || blocked}
              aria-label={`${feature.label} for ${module.name}`}
              onCheckedChange={(next) =>
                onChange([
                  {
                    moduleId: module.id,
                    enabled: true,
                    options: { ...(row?.options ?? {}), [feature.key]: next },
                  },
                ])
              }
            />
          </div>
        );
      })}
    </div>
  );
};
